import { OutreachProgram } from "../types/outreach";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Users, MapPin, Calendar, ClipboardList } from "lucide-react";

interface ProgramDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  program: OutreachProgram;
}

export function ProgramDetailsDialog({
  open,
  onOpenChange,
  program,
}: ProgramDetailsDialogProps) {
  const statusVariants = {
    planned: "secondary",
    active: "default",
    completed: "outline",
    cancelled: "destructive",
  } as const;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Program Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold">{program.name}</h3>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="secondary" className="capitalize">
                {program.type.replace("-", " ")}
              </Badge>
              <Badge
                variant={statusVariants[program.status]}
                className="capitalize"
              >
                {program.status}
              </Badge>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Start Date</p>
                <p>{format(new Date(program.startDate), "MMM d, yyyy")}</p>
              </div>
            </div>
            {program.endDate && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">End Date</p>
                  <p>{format(new Date(program.endDate), "MMM d, yyyy")}</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Location</p>
              <p>{program.location.name}</p>
              <p className="text-sm text-muted-foreground">
                {program.location.address}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Participants</p>
              <p>
                {program.actualParticipants || 0} /{" "}
                {program.targetParticipants} target
              </p>
            </div>
          </div>

          {program.description && (
            <div className="flex items-start gap-2">
              <ClipboardList className="h-4 w-4 text-muted-foreground mt-1" />
              <div>
                <p className="text-sm text-muted-foreground">Description</p>
                <p className="whitespace-pre-wrap">{program.description}</p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
